'use client';

/**
 * usePaymentSupport / useVisibleKinds
 *
 * Thin hooks that resolve the async `isSupported()` checks from the gateway
 * registry so screens can consume the results synchronously after resolution,
 * showing a loading skeleton until then.
 */

import { useState, useEffect } from 'react';
import type { PaymentMethodKind } from '../types';
import { isPaymentMethodSupported, getVisibleKinds } from '../config/gatewayRegistry';
import { useResetOnChange } from './useResetOnChange';

interface PaymentSupportResult {
  /** Map of kind → supported (true/false). Empty while loading. */
  supportMap: Record<string, boolean>;
  /** Whether support checks are still resolving */
  loading: boolean;
}

interface VisibleKindsResult {
  /** Kinds visible for the current PAYG mode, filtered by device support and sorted by renderOrder */
  visibleKinds: PaymentMethodKind[];
  /** Whether the async resolution is still in progress */
  loading: boolean;
}

/**
 * Resolve `isPaymentMethodSupported` for every unique kind found in `items`.
 *
 * Accepts any array of objects with a `kind` property (PaymentMethod[], PaymentOption[], etc.)
 * so callers can pass their data directly without extracting or memoizing kinds.
 */
export const usePaymentSupport = (items: Array<{ kind: string }>): PaymentSupportResult => {
  const [supportMap, setSupportMap] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);

  const kindsKey = items.map((i) => i.kind).join(',');

  useResetOnChange(kindsKey, () => {
    setSupportMap({});
    setLoading(true);
  });

  useEffect(() => {
    let cancelled = false;

    const uniqueKinds = Array.from(new Set(kindsKey.split(',').filter(Boolean)));
    Promise.all(
      uniqueKinds.map(async (kind) => [kind, await isPaymentMethodSupported(kind)] as const)
    ).then((results) => {
      if (!cancelled) {
        setSupportMap(Object.fromEntries(results));
        setLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [kindsKey]);

  return { supportMap, loading };
};

/**
 * Resolve `getVisibleKinds` for the current PAYG mode.
 *
 * Wraps the async registry helper so screens can consume the result
 * synchronously after resolution, showing a loading skeleton until then.
 */
export const useVisibleKinds = (isPayg: boolean): VisibleKindsResult => {
  const [visibleKinds, setVisibleKinds] = useState<PaymentMethodKind[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    getVisibleKinds(isPayg).then((kinds) => {
      if (!cancelled) {
        setVisibleKinds(kinds);
        setLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [isPayg]);

  return { visibleKinds, loading };
};
